import { useEffect, useState } from "react";
import { Page, Composer, Skeleton } from "../components/ui.jsx";
import { readList, appendItem, stamp } from "../lib/storage.js";
import { rankOf, LEVEL_LABEL } from "../data/levels.js";
import { LINKS } from "../data/site.js";
import { badgePermissionNeeded, requestBadgePermission, setBadge } from "../lib/badge.js";
import { loadArchive, refFor, setArchived } from "../lib/archive.js";

const KIND_LABEL = {
  testimony: "🔥 Testimony",
  prayer: "🙏 Prayer request",
  meeting: "🤝 Meeting request",
  checkin: "💛 Check-in",
};

/**
 * A dashboard section that can be folded away.
 *
 * The dashboard is a stack of five streams and most people care about two of
 * them on any given day. Collapsing is remembered per section in localStorage:
 * it is a view preference on one device, not something worth a server round
 * trip, and losing it costs a tap.
 *
 * The heading is a div with a button role rather than a real <button>, because
 * a <button> may not contain an <h3> and the heading has to stay a heading for
 * anything reading the page structure.
 */
function Block({ id, title, sub, count, top, children }) {
  const KEY = "hub:sec:" + id;
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(KEY) !== "0";
    } catch {
      return true;
    }
  });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(KEY, next ? "1" : "0");
    } catch {
      /* private mode; the section just reopens next visit */
    }
  };

  return (
    <>
      <div className="block" style={top ? { marginTop: 32 } : undefined}>
        <div
          className="sec-head"
          role="button"
          tabIndex={0}
          aria-expanded={open}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggle();
            }
          }}
        >
          <span className="sec-chev" aria-hidden="true">
            ▾
          </span>
          <div className="sec-copy">
            <h3>{title}</h3>
            {sub && <p>{sub}</p>}
          </div>
          {/* Only when folded: an open section already shows what it holds. */}
          {!open && count > 0 && <span className="sec-count">{count}</span>}
        </div>
      </div>
      {open && children}
    </>
  );
}

/** Wraps one row with its archive control. */
function Archivable({ refId, archived, onToggle, children }) {
  return (
    <div className={"arch-wrap" + (archived ? " arch-row" : "")}>
      {children}
      <button
        className="arch-btn"
        onClick={() => onToggle(refId, !archived)}
        aria-label={archived ? "Restore this" : "Archive this"}
      >
        {archived ? "Restore" : "Archive"}
      </button>
    </div>
  );
}

/**
 * The "and n you've archived" line, plus what it reveals.
 *
 * Archiving hides rather than deletes, so every section that can hide things
 * has to be able to show them again — otherwise the button is a delete button
 * wearing a friendlier word.
 */
function Archived({ items, render }) {
  const [show, setShow] = useState(false);
  if (!items.length) return null;
  return (
    <>
      <button className="arch-toggle" onClick={() => setShow(!show)}>
        {show ? "▾ Hide archived" : `▸ Show ${items.length} archived`}
      </button>
      {show && items.map(render)}
    </>
  );
}

// One plain message row — a broadcast, or a reply addressed to you.
function Msg({ name, ts, text, kind }) {
  return (
    <div className={"msg" + (kind ? " " + kind : "")}>
      <span className="mname">{name || "Anonymous"}</span>
      <span className="mtime">{stamp(ts)}</span>
      {kind && <span className="mtime">· {KIND_LABEL[kind] || kind}</span>}
      <div className="mtext">{text}</div>
    </div>
  );
}

// One inbox item plus its replies, with an inline reply box for directors.
function InboxItem({ item, replies, userName }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sent, setSent] = useState([]);

  const all = [...replies, ...sent];

  const send = async () => {
    if (!text.trim()) return;
    // toTs threads the reply; toPid/toName is how the server decides who may read it.
    const reply = {
      toTs: item.ts,
      toName: item.name,
      toPid: item.pid,
      from: userName,
      text: text.trim(),
      ts: Date.now(),
    };
    await appendItem("inbox-replies", reply);
    setSent([...sent, reply]);
    setText("");
    setOpen(false);
  };

  return (
    <div className={"msg" + (item.kind ? " " + item.kind : "")}>
      <span className="mname">{item.name || "Anonymous"}</span>
      <span className="mtime">{stamp(item.ts)}</span>
      {item.kind && (
        <span className="mtime">· {KIND_LABEL[item.kind] || item.kind}</span>
      )}
      <div className="mtext">{item.text}</div>

      {all.map((r, i) => (
        <div key={i} className="reply">
          <span className="mname">↩ {r.from}</span>
          <span className="mtime">{stamp(r.ts)}</span>
          <div className="mtext">{r.text}</div>
        </div>
      ))}

      {open ? (
        <div style={{ marginTop: 12 }}>
          <textarea
            className="finput"
            style={{ minHeight: 70, marginTop: 0 }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Reply to ${item.name}…`}
            autoFocus
          />
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <button className="btn-post" onClick={send} disabled={!text.trim()}>
              Send reply
            </button>
            <button
              className="lockout"
              style={{ marginTop: 0 }}
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button className="chatlink" onClick={() => setOpen(true)}>
          ↩ Reply
        </button>
      )}
    </div>
  );
}

// One serving date or scheduling request.
function ScheduleRow({ item, pending }) {
  return (
    <div className={"sched" + (pending ? " pending" : "")}>
      <span className="sdate">{item.dates || "Date TBC"}</span>
      <span className="sbody">
        <span className="sname">
          {item.position}
          {item.team ? ` · ${item.team}` : ""}
        </span>
        {(item.serviceType || item.times) && (
          <span className="smeta">
            {[item.serviceType, item.times].filter(Boolean).join(" · ")}
          </span>
        )}
      </span>
    </div>
  );
}

export default function Dashboard({ back, go, userName, userRank, level, pid }) {
  const [inbox, setInbox] = useState([]);
  const [replies, setReplies] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [schedule, setSchedule] = useState(null);
  // Reports with activity since this person last looked — filed, updated, or
  // one they have just been named on. The server decides which of those they
  // may see at all, so nothing is filtered here.
  const [newIncidents, setNewIncidents] = useState([]);
  // iOS only draws the icon badge once notification permission is granted.
  // Offered as a row rather than prompted on load: a permission dialog nobody
  // asked for is the fastest way to get it denied for good.
  const [offerBadge, setOfferBadge] = useState(false);
  // Refs this person has dealt with. Held server-side, so it follows them.
  const [archive, setArchive] = useState(() => new Set());

  const isDirector = userRank >= rankOf("director");

  useEffect(() => {
    Promise.all([
      readList("leadership-inbox"),
      readList("inbox-replies"),
      readList("broadcasts"),
    ]).then(([i, r, b]) => {
      setInbox(i);
      setReplies(r);
      setBroadcasts(b);
      setLoading(false);
    });

    // Loaded separately so a slow Planning Center call never holds up the rest
    // of the dashboard.
    fetch("/api/schedule")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setSchedule(d || { serving: [], requests: [] }))
      .catch(() => setSchedule({ serving: [], requests: [], unavailable: true }));

    // Opening the dashboard is what marks streams read, so this has to be
    // captured before that lands — otherwise a report filed a minute ago is
    // cleared on the way in and never surfaces.
    fetch("/api/notifications")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setNewIncidents(d?.incidents || []))
      .catch(() => {});

    loadArchive().then(setArchive);

    setOfferBadge(badgePermissionNeeded());
  }, []);

  /**
   * Hide or restore one row.
   *
   * Applied locally first so the row moves under the finger, then written. A
   * failed write is rolled back rather than left to look as though it worked —
   * an archive that silently comes back next visit is worse than one that
   * refuses now.
   */
  const toggleArchive = async (ref, on) => {
    setArchive((current) => {
      const next = new Set(current);
      if (on) next.add(ref);
      else next.delete(ref);
      return next;
    });
    const ok = await setArchived(ref, on);
    if (!ok) {
      setArchive((current) => {
        const next = new Set(current);
        if (on) next.delete(ref);
        else next.add(ref);
        return next;
      });
    }
  };

  const isArchived = (ref) => archive.has(ref);

  /** Splits a list into what to show and what has been put away. */
  const split = (items, ref) => ({
    live: items.filter((i) => !isArchived(ref(i))),
    filed: items.filter((i) => isArchived(ref(i))),
  });

  const turnOnBadge = async () => {
    const granted = await requestBadgePermission();
    setOfferBadge(false);
    if (granted) {
      // Write it immediately so the icon is correct without waiting for the
      // next visit.
      const d = await fetch("/api/notifications")
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      setBadge(d?.total || 0);
    }
  };

  // The server has already removed anything this person may not read. This
  // narrows what is left to incoming only, since replies they wrote themselves
  // belong under the item they answered, not under "Replies to you".
  const mine = replies.filter((r) =>
    r.toPid && pid ? r.toPid === pid : (r.toName || "").toLowerCase() === userName.toLowerCase(),
  );

  const requests = schedule?.requests || [];
  const reqRef = (r) => refFor("request", r.id);
  const bcRef = (b) => refFor("broadcast", b.ts);
  const inRef = (i) => refFor("inbox", i.ts);
  const rpRef = (r) => refFor("reply", r.ts);

  const reqs = split(requests, reqRef);
  const bcs = split(broadcasts.slice().reverse(), bcRef);
  const ins = split(inbox.slice().reverse(), inRef);
  const rps = split(mine.slice().reverse(), rpRef);

  const row = (node, ref) => (
    <Archivable key={ref} refId={ref} archived={isArchived(ref)} onToggle={toggleArchive}>
      {node}
    </Archivable>
  );

  return (
    <Page
      back={back}
      eyebrow={LEVEL_LABEL[level]}
      title={`${userName !== "Anonymous" ? userName + "'s" : "Your"} dashboard.`}
      keepCase
      intro="Everything addressed to you, in one place — no digging."
    >
      {offerBadge && (
        <button className="card badge-optin" onClick={turnOnBadge}>
          <div className="name">
            <span>🔔 Show a count on the app icon</span>
            <span className="arrow">→</span>
          </div>
          <div className="desc">
            Puts the number of things waiting for you on the icon, the way Mail and
            Messages do. Needs one tap to allow it.
          </div>
        </button>
      )}

      {/* ---- incidents, first because they are the most time-sensitive ----
          Not archivable: an incident alert clears itself once the report has
          been opened, and a safeguarding report is not something to tidy away
          from the one screen that surfaces it. */}
      {newIncidents.length > 0 && (
        <>
          <div className="block">
            <h3>🚨 Incident {newIncidents.length === 1 ? "report" : "reports"}</h3>
            <p>
              New, or updated since you last opened this. Only reports you have access
              to appear here.
            </p>
          </div>
          {newIncidents.map((i) => (
            <button
              key={i.id}
              className="card inc-alert"
              onClick={() => go && go("incidents")}
            >
              <div className="name">
                <span>{i.category}</span>
                <span className="inc-status open">
                  {i.kind === "update" ? "Updated" : "New"}
                </span>
              </div>
              <div className="desc">
                {/* The filing date for a report they have never seen, the
                    activity date for movement on one they have. Showing the
                    activity time under a "Filed" label would misdate the
                    record on the one screen that surfaces it. */}
                {i.kind === "update" ? "Updated " : "Filed "}
                {new Date(
                  i.kind === "update" ? i.at || i.filedAt : i.filedAt,
                ).toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}{" "}
                · {i.id}
              </div>
            </button>
          ))}
        </>
      )}

      {/* ---- Planning Center schedule ---- */}
      <Block
        id="requests"
        title="🙋 Requests waiting on you"
        sub="Scheduling requests from Planning Center that still need a yes or no."
        count={reqs.live.length}
      >
        {!schedule ? (
          <Skeleton />
        ) : schedule.unavailable ? (
          <div className="empty-note">
            Couldn't reach Planning Center just now. Try again shortly.
          </div>
        ) : schedule.linked === false ? (
          <div className="empty-note">
            Connect your Planning Center account to see your schedule here.
          </div>
        ) : requests.length === 0 ? (
          <div className="empty-note">Nothing waiting on you. 🙌</div>
        ) : (
          <>
            {reqs.live.length === 0 ? (
              <div className="empty-note">
                All caught up — everything here is archived.
              </div>
            ) : (
              reqs.live.map((i) => row(<ScheduleRow item={i} pending />, reqRef(i)))
            )}
            <Archived
              items={reqs.filed}
              render={(i) => row(<ScheduleRow item={i} pending />, reqRef(i))}
            />
            {/* Responding has to happen in Planning Center — accepting on
                someone's behalf is not ours to do. Archiving one here only
                stops it counting; it does not answer it. */}
            {reqs.live.length > 0 && (
              <a
                className="card"
                href={LINKS.servingSchedule}
                target="_blank"
                rel="noreferrer"
              >
                <div className="name">
                  <span>Respond in Planning Center</span>
                  <span className="arrow">↗</span>
                </div>
              </a>
            )}
          </>
        )}
      </Block>

      <Block
        id="serving"
        title="📅 You're serving"
        sub="Your confirmed dates, newest first."
        count={schedule?.serving?.length || 0}
        top
      >
        {!schedule ? (
          <Skeleton />
        ) : schedule.linked === false ? (
          <div className="empty-note">
            Connect your Planning Center account to see your serving dates.
          </div>
        ) : schedule.serving.length === 0 ? (
          <div className="empty-note">Nothing on the schedule yet.</div>
        ) : (
          schedule.serving.map((i) => <ScheduleRow key={i.id} item={i} />)
        )}
      </Block>

      <Block
        id="broadcasts"
        title="📣 Urgent from leadership"
        count={bcs.live.length}
        top
      >
        {isDirector && (
          <Composer
            placeholder="Post an urgent update to the room…"
            busyLabel="Post update"
            onPost={async (text) =>
              setBroadcasts(
                await appendItem("broadcasts", { name: userName, text, ts: Date.now() }),
              )
            }
          />
        )}
        {loading ? (
          <Skeleton />
        ) : bcs.live.length === 0 ? (
          <div className="empty-note">No urgent updates right now. All clear.</div>
        ) : (
          bcs.live.map((m) => row(<Msg name={m.name} ts={m.ts} text={m.text} />, bcRef(m)))
        )}
        <Archived
          items={bcs.filed}
          render={(m) => row(<Msg name={m.name} ts={m.ts} text={m.text} />, bcRef(m))}
        />
      </Block>

      {(isDirector || inbox.length > 0) && (
        <Block
          id="inbox"
          title="📥 Your inbox"
          sub={
            isDirector
              ? "Meeting requests, private prayer, and check-ins addressed to you — reply right here. Honor discretion (Code 13)."
              : "Requests you've sent, and any reply to them."
          }
          count={ins.live.length}
          top
        >
          {loading ? (
            <Skeleton />
          ) : ins.live.length === 0 ? (
            <div className="empty-note">Nothing waiting on you. 🙌</div>
          ) : (
            ins.live.map((item) =>
              row(
                <InboxItem
                  item={item}
                  replies={replies.filter((r) => r.toTs === item.ts)}
                  userName={userName}
                />,
                inRef(item),
              ),
            )
          )}
          <Archived
            items={ins.filed}
            render={(item) =>
              row(
                <InboxItem
                  item={item}
                  replies={replies.filter((r) => r.toTs === item.ts)}
                  userName={userName}
                />,
                inRef(item),
              )
            }
          />
        </Block>
      )}

      <Block
        id="replies"
        title="🔔 Replies to you"
        sub="When leadership responds to something you sent, it lands here."
        count={rps.live.length}
        top
      >
        {loading ? (
          <Skeleton />
        ) : rps.live.length === 0 ? (
          <div className="empty-note">No replies yet — you're all caught up.</div>
        ) : (
          rps.live.map((r) => row(<Msg name={r.from} ts={r.ts} text={r.text} />, rpRef(r)))
        )}
        <Archived
          items={rps.filed}
          render={(r) => row(<Msg name={r.from} ts={r.ts} text={r.text} />, rpRef(r))}
        />
      </Block>
    </Page>
  );
}
